import { useEffect, useRef, useState } from 'react';
import { contact, formatFullAddress } from '@/content/et/contact';
import { useMapsCta } from '@/lib/mapsCta';
import styles from './ContactSection.module.css';

const MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? '';

/** Monochrome — slightly lifted greys so roads read under a light scrim */
const MONO_STYLES: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#161618' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#b0b0b4' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#161618' }] },
  {
    featureType: 'administrative',
    elementType: 'geometry',
    stylers: [{ color: '#2e2e32' }],
  },
  {
    featureType: 'administrative.country',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#9a9a9e' }],
  },
  {
    featureType: 'administrative.locality',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#c4c4c8' }],
  },
  {
    featureType: 'poi',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: '#3a3a40' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#222226' }],
  },
  {
    featureType: 'road',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#a8a8ae' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [{ color: '#4a4a52' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#222226' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#d0d0d4' }],
  },
  {
    featureType: 'transit',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#0e0e10' }],
  },
  {
    featureType: 'water',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#6a6a70' }],
  },
];

/** Classic pin path (viewBox-ish 0–24) for a clear destination mark */
const PIN_PATH =
  'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z';

declare global {
  interface Window {
    __lodvikMapsReady?: Promise<void>;
  }
}

function loadMapsScript(key: string): Promise<void> {
  if (typeof google !== 'undefined' && google.maps) {
    return Promise.resolve();
  }
  if (window.__lodvikMapsReady) return window.__lodvikMapsReady;

  window.__lodvikMapsReady = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&language=et`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error('Google Maps skripti laadimine ebaõnnestus'));
    document.head.appendChild(script);
  });

  return window.__lodvikMapsReady;
}

type ContactMapProps = {
  /** Full-bleed section background vs framed block */
  variant?: 'backdrop' | 'inline';
};

export function ContactMap({ variant = 'inline' }: ContactMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mapsCta = useMapsCta();

  useEffect(() => {
    if (!MAPS_KEY) {
      setError('Kaardi API võti puudub');
      return;
    }
    if (!containerRef.current) return;

    let cancelled = false;
    let resizeObserver: ResizeObserver | null = null;

    loadMapsScript(MAPS_KEY)
      .then(() => {
        if (cancelled || !containerRef.current || !google?.maps) return;

        const center = {
          lat: contact.mapLat,
          lng: contact.mapLng,
        };

        /**
         * Desktop: mapZoom.
         * Mobile: ~40% more zoomed out vs desktop (20% then +20% more → scale × 0.64).
         */
        const resolveZoom = () => {
          const base = contact.mapZoom;
          const mobile = window.matchMedia('(max-width: 767px)').matches;
          return mobile ? base + Math.log2(0.64) : base;
        };

        const map = new google.maps.Map(containerRef.current, {
          center,
          zoom: resolveZoom(),
          isFractionalZoomEnabled: true,
          disableDefaultUI: true,
          keyboardShortcuts: false,
          clickableIcons: false,
          // Backdrop is atmospheric — pan/zoom only with ctrl/two-finger
          gestureHandling: variant === 'backdrop' ? 'cooperative' : 'cooperative',
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          zoomControl: false,
          scaleControl: false,
          rotateControl: false,
          panControl: false,
          mapTypeId: 'roadmap',
          styles: MONO_STYLES,
          backgroundColor: '#121212',
        });

        mapRef.current = map;

        // Soft halo so the pin reads on the monochrome map
        new google.maps.Marker({
          map,
          position: center,
          clickable: false,
          zIndex: 1,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: variant === 'backdrop' ? 12 : 10,
            fillColor: '#ffffff',
            fillOpacity: 0.16,
            strokeColor: '#ffffff',
            strokeOpacity: 0.5,
            strokeWeight: 1,
          },
        });

        // Clear destination pin (not a small dot)
        new google.maps.Marker({
          map,
          position: center,
          title: formatFullAddress(),
          zIndex: 2,
          icon: {
            path: PIN_PATH,
            fillColor: '#ffffff',
            fillOpacity: 1,
            strokeColor: '#0a0a0a',
            strokeWeight: 1.25,
            scale: variant === 'backdrop' ? 1.65 : 1.35,
            anchor: new google.maps.Point(12, 22),
          },
        });

        /**
         * Place the destination at the centre of #kontakt-map-focus —
         * the reserved band below the contact cards (all breakpoints).
         * panBy moves the map; the pin moves the opposite direction.
         */
        const applyOffset = () => {
          if (!mapRef.current || !containerRef.current) return;
          google.maps.event.trigger(mapRef.current, 'resize');
          mapRef.current.setZoom(resolveZoom());
          mapRef.current.setCenter(center);

          if (variant !== 'backdrop') return;

          const stage = containerRef.current;
          const focus = document.getElementById('kontakt-map-focus');
          const stageRect = stage.getBoundingClientRect();

          let targetX = stageRect.width / 2;
          let targetY = stageRect.height * 0.78;

          if (focus) {
            const focusRect = focus.getBoundingClientRect();
            // Only use focus band if it has real layout size
            if (focusRect.height > 40 && focusRect.width > 40) {
              targetX =
                focusRect.left + focusRect.width / 2 - stageRect.left;
              targetY =
                focusRect.top + focusRect.height / 2 - stageRect.top;
            }
          }

          const centerX = stageRect.width / 2;
          const centerY = stageRect.height / 2;
          mapRef.current.panBy(
            Math.round(centerX - targetX),
            Math.round(centerY - targetY),
          );
        };

        google.maps.event.addListenerOnce(map, 'idle', () => {
          applyOffset();
          // Re-apply after layout settles (cards + focus band flex height)
          requestAnimationFrame(() => {
            applyOffset();
            requestAnimationFrame(applyOffset);
          });
        });

        resizeObserver = new ResizeObserver(() => {
          applyOffset();
        });
        resizeObserver.observe(containerRef.current);
        const focusEl = document.getElementById('kontakt-map-focus');
        if (focusEl) resizeObserver.observe(focusEl);
        const sectionEl = document.getElementById('kontakt');
        if (sectionEl) resizeObserver.observe(sectionEl);
      })
      .catch(() => {
        if (!cancelled) setError('Kaarti ei õnnestunud laadida');
      });

    return () => {
      cancelled = true;
      resizeObserver?.disconnect();
      mapRef.current = null;
    };
  }, [variant]);

  const frameClass =
    variant === 'backdrop' ? styles.mapFrameBackdrop : styles.mapFrame;

  return (
    <div
      className={
        variant === 'backdrop' ? styles.mapBlockBackdrop : styles.mapBlock
      }
      aria-hidden={variant === 'backdrop' ? true : undefined}
    >
      <div
        className={frameClass}
        role={variant === 'backdrop' ? undefined : 'img'}
        aria-label={
          variant === 'backdrop'
            ? undefined
            : `${contact.legalName} — asukoht kaardil: ${formatFullAddress()}`
        }
      >
        {error ? (
          <div className={styles.mapFallback}>
            {variant === 'inline' && (
              <>
                <p>{error}</p>
                <a
                  href={mapsCta.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {mapsCta.label}
                </a>
              </>
            )}
          </div>
        ) : (
          <div ref={containerRef} className={styles.mapCanvas} />
        )}
      </div>
    </div>
  );
}
